import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatINRExact } from "../lib/api";
import { loadRazorpaySdk } from "../lib/razorpay";
import { Button } from "../components/ui/button";
import { CheckCircle2, Lock, ShieldCheck, AlertTriangle, BadgePercent } from "lucide-react";
import { toast, Toaster } from "sonner";

/** Public pay-now page reached from a WhatsApp / email payment-retry link.
 *  No login required — the token in the URL is the credential. */
export default function PayNow() {
    const { token } = useParams();
    const [info, setInfo] = useState(null);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(null);
    const [usePrepaid, setUsePrepaid] = useState(true);

    const offer = info?.prepaid_offer || null;
    const prepaidOn = !!offer && usePrepaid;
    const amountDue = prepaidOn ? offer.new_total : info?.amount;

    useEffect(() => {
        api.get(`/pay/${token}`)
            .then((r) => setInfo(r.data))
            .catch((e) => setError(e.response?.data?.detail || "This payment link is no longer valid."));
    }, [token]);

    const pay = async () => {
        setBusy(true);
        try {
            const loaded = await loadRazorpaySdk();
            if (!loaded) throw new Error("Could not reach the payment gateway. Check your connection.");
            const { data: opt } = await api.post(`/pay/${token}/intent`, { switch_to_prepaid: prepaidOn });
            await new Promise((resolve, reject) => {
                const rzp = new window.Razorpay({
                    key: opt.key_id,
                    amount: opt.amount,
                    currency: opt.currency,
                    order_id: opt.razorpay_order_id,
                    name: opt.name,
                    description: opt.description,
                    prefill: opt.prefill,
                    theme: { color: "#0369a1" },
                    handler: async (res) => {
                        try {
                            const { data } = await api.post(`/pay/${token}/confirm`, {
                                razorpay_order_id: res.razorpay_order_id,
                                razorpay_payment_id: res.razorpay_payment_id,
                                razorpay_signature: res.razorpay_signature,
                            });
                            setDone(data);
                            resolve();
                        } catch (e) {
                            reject(new Error(e.response?.data?.detail || "Payment captured but confirmation failed. Contact support."));
                        }
                    },
                    modal: { ondismiss: () => reject(new Error("Payment cancelled.")) },
                });
                rzp.on("payment.failed", (r) => reject(new Error(r?.error?.description || "Payment failed.")));
                rzp.open();
            });
            toast.success("Payment successful!");
        } catch (e) {
            toast.error(e.message);
        } finally {
            setBusy(false);
        }
    };

    if (error) {
        return (
            <div className="max-w-lg mx-auto px-4 py-24 text-center" data-testid="pay-link-error">
                <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-4" />
                <h1 className="font-serif text-3xl text-stone-900">Link unavailable</h1>
                <p className="text-sm text-stone-600 mt-3">{error}</p>
                <Link to="/shop" className="text-sm text-sky-700 hover:underline mt-6 inline-block">Continue shopping</Link>
            </div>
        );
    }
    if (!info) return <div className="max-w-lg mx-auto px-4 py-24 text-center text-stone-500">Loading your payment…</div>;

    const paid = done || info.paid;

    return (
        <div className="max-w-lg mx-auto px-4 py-12" data-testid="pay-now-page">
            <Toaster position="top-center" />

            {paid ? (
                <div className="bg-white border border-emerald-300 p-8 text-center" data-testid="pay-success">
                    <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
                    <h1 className="font-serif text-3xl text-stone-900">Payment received</h1>
                    <p className="text-sm text-stone-600 mt-3">
                        Order <span className="font-mono font-semibold">#{done?.reference || info.reference}</span> is confirmed.
                        {done?.prepaid_applied && (
                            <span className="block mt-2 text-emerald-700 font-medium" data-testid="pay-success-saving">
                                You saved {formatINRExact(done.prepaid_discount)} by paying online.
                            </span>
                        )}
                    </p>
                    <Link to="/orders" className="inline-block mt-6 text-sm text-sky-700 hover:underline">View my orders</Link>
                </div>
            ) : (
                <>
                    <div className="label-eyebrow text-sky-700">{info.brand}</div>
                    <h1 className="font-serif text-4xl text-stone-900 mt-2">Complete your payment</h1>
                    <p className="text-sm text-stone-600 mt-2">
                        {info.kind === "cart"
                            ? "Your cart was saved. Pay now and we'll ship it straight away."
                            : `Balance due on order #${info.reference}.`}
                    </p>

                    {offer && (
                        <div className="mt-6 bg-emerald-50 border border-emerald-300 p-5" data-testid="prepaid-switch-offer">
                            <div className="flex items-start gap-3">
                                <BadgePercent className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <div className="font-serif text-xl text-emerald-900">
                                        Switch to online payment and save {formatINRExact(offer.discount)}
                                    </div>
                                    <p className="text-xs text-emerald-800 mt-1 leading-relaxed">
                                        This order was placed as Cash on Delivery. Pay now with UPI, card or netbanking and we'll
                                        take <strong>{offer.pct}% off</strong> — {formatINRExact(info.amount)} becomes <strong>{formatINRExact(offer.new_total)}</strong>.
                                        The discount is applied to your order the moment the payment goes through.
                                    </p>
                                    <div className="flex gap-4 mt-3 text-xs">
                                        <label className="flex items-center gap-2 cursor-pointer" data-testid="prepaid-opt-in">
                                            <input type="radio" checked={usePrepaid} onChange={() => setUsePrepaid(true)} className="accent-emerald-600" />
                                            <span className="text-emerald-900 font-medium">Yes, apply {offer.pct}% off</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer" data-testid="prepaid-opt-out">
                                            <input type="radio" checked={!usePrepaid} onChange={() => setUsePrepaid(false)} className="accent-stone-500" />
                                            <span className="text-stone-600">Pay the full {formatINRExact(info.amount)}</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="bg-white border border-stone-200 mt-6">
                        <div className="p-5 space-y-2">
                            {(info.items || []).map((it, i) => (
                                <div key={i} className="flex justify-between gap-4 text-sm border-b border-stone-100 pb-2 last:border-0" data-testid={`pay-item-${i}`}>
                                    <span className="text-stone-700">
                                        {it.product_name} <span className="text-stone-400">{it.size_label} × {it.quantity}</span>
                                    </span>
                                    <span className="font-mono">{formatINRExact((it.price || 0) * (it.quantity || 0))}</span>
                                </div>
                            ))}
                        </div>
                        <div className="px-5 py-4 bg-stone-50/60 border-t border-stone-200 space-y-1.5 text-sm">
                            {info.subtotal != null && <Line label="Subtotal" value={formatINRExact(info.subtotal)} />}
                            {info.shipping != null && <Line label="Shipping" value={info.shipping === 0 ? "Free" : formatINRExact(info.shipping)} />}
                            {info.discount > 0 && <Line label="Coupon discount" value={`− ${formatINRExact(info.discount)}`} accent />}
                            {info.prepaid_discount > 0 && <Line label="Prepaid discount" value={`− ${formatINRExact(info.prepaid_discount)}`} accent />}
                            {prepaidOn && (
                                <div className="flex justify-between text-emerald-700 font-medium" data-testid="prepaid-discount-line">
                                    <span>Prepaid Discount ({offer.pct}%)</span>
                                    <span className="font-mono">− {formatINRExact(offer.discount)}</span>
                                </div>
                            )}
                            <div className="flex justify-between font-serif text-2xl pt-2 border-t border-stone-200">
                                <span>Amount due</span>
                                <span data-testid="pay-amount">{formatINRExact(amountDue)}</span>
                            </div>
                        </div>
                    </div>

                    {info.gateway_ready ? (
                        <Button
                            onClick={pay}
                            disabled={busy}
                            className="rounded-none w-full mt-6 py-6 bg-sky-700 hover:bg-sky-800 text-stone-50"
                            data-testid="pay-now-btn"
                        >
                            <Lock className="w-4 h-4 mr-2" />
                            {busy ? "Opening secure payment…" : `Pay ${formatINRExact(amountDue)} now${prepaidOn ? ` · saving ${formatINRExact(offer.discount)}` : ""}`}
                        </Button>
                    ) : (
                        <div className="mt-6 bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900">
                            Online payment is temporarily unavailable. Please contact us to complete this order.
                        </div>
                    )}

                    <p className="text-[11px] text-stone-500 mt-3 flex items-center justify-center gap-1.5">
                        <ShieldCheck className="w-3.5 h-3.5" />
                        UPI, cards, netbanking & wallets · secured by Razorpay
                    </p>
                </>
            )}
        </div>
    );
}

const Line = ({ label, value, accent = false }) => (
    <div className={`flex justify-between ${accent ? "text-emerald-700" : ""}`}>
        <span className={accent ? "" : "text-stone-600"}>{label}</span>
        <span className="font-mono">{value}</span>
    </div>
);
